import { useEffect, useState } from "react";
import axios from "axios";

// Who may delete an overview row (exploration, selection, reproduction).
//
// The backend rule (oraclaseapp/deletion_rights.py): the creator
// (``row.auth_user``) may delete, an administrator may delete anybody's, a
// row without a creator is an administrator's. The overview pages disable
// the delete button up front and warn an administrator before deleting
// another account's data; the server enforces the same rule on every
// delete route.

/**
 * The signed-in account as the delete rule sees it: its id and administrator
 * flag from api/user/profile/ (the stored userId, no administrator rights,
 * until the profile has answered).
 */
export function useDeleteRights() {
  const [me, setMe] = useState(() => ({
    userId: localStorage.getItem("userId"),
    isAdmin: false,
  }));

  useEffect(() => {
    let cancelled = false;
    // Through a promise chain: a lookup that throws or answers with no
    // promise (stubbed API) leaves the page rendered with the stored id.
    Promise.resolve()
      .then(() => axios.get("api/user/profile/"))
      .then((res) => {
        if (cancelled) return;
        const id = res?.data?.id;
        setMe({
          userId: id != null ? String(id) : localStorage.getItem("userId"),
          isAdmin: Boolean(res?.data?.is_admin),
        });
      })
      .catch((err) => {
        console.error(err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return me;
}

/**
 * The rights of `me` (from useDeleteRights) on `row`:
 * - `allowed`: whether the delete button is enabled;
 * - `reason`: the tooltip when it is not;
 * - `warning`: what an administrator is told in the dialog when the row is
 *   not theirs (null otherwise).
 * `kind` names the row in the texts ("exploration", "selection", ...).
 */
export function deleteRights(row, me, kind) {
  const ownerId = row?.auth_user;
  const creator = row?.started_by_email || null;
  const mine =
    ownerId != null && me?.userId != null && String(ownerId) === String(me.userId);
  if (mine) return { allowed: true, reason: null, warning: null };
  if (me?.isAdmin) {
    return {
      allowed: true,
      reason: null,
      warning: creator
        ? `Created by ${creator}. You are deleting another user's ${kind} as an administrator.`
        : `No creator recorded. You are deleting this ${kind} as an administrator.`,
    };
  }
  return {
    allowed: false,
    reason: creator
      ? `Created by ${creator} — only the creator can delete it`
      : "No creator recorded — only an administrator can delete it",
    warning: null,
  };
}
